
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import TableauProgression from '@/components/checklists/TableauProgression';
import FormulaireCreationChecklist from '@/components/checklists/FormulaireCreationChecklist';
import FormulaireModificationChecklist from '@/components/checklists/FormulaireModificationChecklist';
import ListeChecklistsNouvelles from '@/components/checklists/ListeChecklistsNouvelles';
import { useNouvellesChecklists, ChecklistWithTaches } from '@/hooks/useNouvellesChecklists';
import { useProAccess } from '@/hooks/useProAccess';
import { Clipboard, Crown } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import PremiumLimitBanner from '@/components/premium/PremiumLimitBanner';
import HiddenDataBanner from '@/components/premium/HiddenDataBanner';

const FREE_CHECKLISTS_LIMIT = 1;

const ChecklistsPage = () => {
  const { t } = useTranslation('checklists');
  const { 
    checklists, 
    loading, 
    creerChecklist, 
    modifierTache, 
    supprimerChecklist, 
    reinitialiserChecklist,
    modifierChecklist
  } = useNouvellesChecklists();
  const { isProAccount } = useProAccess();
  const [checklistEnModification, setChecklistEnModification] = useState<ChecklistWithTaches | null>(null);
  const [activeTab, setActiveTab] = useState('checklists');

  const canCreateChecklist = isProAccount || checklists.length < FREE_CHECKLISTS_LIMIT;
  
  const hiddenChecklistsCount = !isProAccount && checklists.length > FREE_CHECKLISTS_LIMIT
    ? checklists.length - FREE_CHECKLISTS_LIMIT
    : 0;

  const checklistsVisibles = !isProAccount 
    ? checklists.slice(0, FREE_CHECKLISTS_LIMIT)
    : checklists;

  const handleCreerChecklist = async (nom: string, type: string, bateauId: string, taches: string[]) => {
    if (!canCreateChecklist) {
      toast({
        title: t('page.limitReachedTitle'),
        description: t('page.limitReachedDescription', { limit: FREE_CHECKLISTS_LIMIT }),
        variant: "destructive",
      });
      return { error: t('page.limitReachedTitle') };
    }
    return creerChecklist(nom, type, bateauId, taches);
  };

  const handleModifierChecklist = async (id: string, nom: string, taches: string[]) => {
    await modifierChecklist(id, nom, taches);
    setChecklistEnModification(null);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nautic mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('page.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 p-4 lg:p-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Clipboard className="h-8 w-8 text-nautic" />
            <div>
              <h1 className="text-xl lg:text-2xl font-bold tracking-tight text-nautic flex items-center gap-2 flex-wrap">
                {t('page.title')}
                {!isProAccount && (
                  <>
                    <Crown className="h-4 lg:h-5 w-4 lg:w-5 text-nautic-gold" />
                    <span className="text-sm lg:text-base text-gray-600">{t('page.freeLimitBadge', { limit: FREE_CHECKLISTS_LIMIT })}</span>
                  </>
                )}
              </h1>
              <p className="text-muted-foreground">
                {t('page.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {!isProAccount && checklists.length >= FREE_CHECKLISTS_LIMIT && (
          <PremiumLimitBanner 
            title={t('page.premiumLimitBannerTitle')}
            description={t('page.premiumLimitBannerDescription', { limit: FREE_CHECKLISTS_LIMIT })}
          />
        )}

        {hiddenChecklistsCount > 0 && (
          <HiddenDataBanner hiddenCount={hiddenChecklistsCount} itemLabel={t('page.hiddenChecklistsLabel')} />
        )}

        <TableauProgression
          checklists={checklistsVisibles}
          onReinitialiser={reinitialiserChecklist}
          onSupprimer={supprimerChecklist}
          onModifier={setChecklistEnModification}
        />

        <Tabs value={activeTab} onValueChange={(value) => {
          if (value === 'creer' && !canCreateChecklist) {
            toast({
              title: t('page.limitReachedTitle'),
              description: t('page.limitReachedDescription', { limit: FREE_CHECKLISTS_LIMIT }),
              variant: "destructive",
            });
            return;
          }
          setActiveTab(value);
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="checklists">{t('page.tabs.myChecklists')}</TabsTrigger>
            <TabsTrigger value="creer" disabled={!canCreateChecklist}>
              {t('page.tabs.create')}
              {!canCreateChecklist && <Crown className="h-3 w-3 ml-1 text-nautic-gold" />}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="checklists" className="space-y-6">
            <ListeChecklistsNouvelles
              checklists={checklistsVisibles}
              onModifierTache={modifierTache}
              onReinitialiser={reinitialiserChecklist}
              onSupprimer={supprimerChecklist}
              onModifierChecklist={setChecklistEnModification}
            />
          </TabsContent>
          
          <TabsContent value="creer" className="space-y-6">
            <FormulaireCreationChecklist onCreerChecklist={handleCreerChecklist} />
          </TabsContent>
        </Tabs>

        {/* Dialog de modification de checklist */}
        <Dialog open={!!checklistEnModification} onOpenChange={() => setChecklistEnModification(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle>{t('page.modifyDialogTitle')}</DialogTitle>
            </DialogHeader>
            {checklistEnModification && (
              <FormulaireModificationChecklist
                checklist={checklistEnModification}
                onModifierChecklist={handleModifierChecklist}
                onAnnuler={() => setChecklistEnModification(null)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default ChecklistsPage;
